import Box from '@mui/material/Box';
import { Subscription } from '@rails/actioncable';
import { useQueryClient } from '@tanstack/react-query';
import { Ticket_Read } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { ROUTE_MAP_HEIGHT_IN_PX } from '~components/Job/RouteMap';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { Skeleton } from '~components/Skeleton/Skeleton';
import {
  approveAllWithTracking,
  trackLoadsApproved,
} from '~hooks/useAnalytics/trackApprovals';
import { useJob } from '~hooks/useJob/useJob';
import { Load } from '~hooks/useLoad/models';
import { BasicMaterial } from '~hooks/useMaterials/models';
import { Ticket } from '~hooks/useTickets/models';
import { makeLoadsQueryContextKey } from '~pages/Approvals/Files/utils';
import connection from '~services/connectionModule';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { shouldAllowUserToAddNewLoads } from '~utils/loads/load-utils';
import { subscribeToChannel } from '~utils/rtu/rtu-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import LoadsTable, { LoadsTableProps } from '../../LoadsTable/LoadsTable';
import TicketDrawerV2 from '../../TicketDrawer/TicketDrawer';
import LoadsQueryContext from './LoadsQueryContext';
import useCrossOrderNavigation, { LOADLESS_JOB } from './useCrossOrderNavigation';

interface LoadsTabProps {
  // Replaces only the table; the ticket drawer and the data refreshing stay with this tab.
  renderLoadsTable?: (props: LoadsTableProps) => React.ReactNode;
}

export default function LoadsTab({ renderLoadsTable }: LoadsTabProps) {
  const { userStore, ticketsStore } = useStores();
  const userPermissions = userStore.getPermissions();

  const { addLoad, approveLoad } = useJob();

  const queryClient = useQueryClient();
  const loadsQuery = useContext(LoadsQueryContext);
  const currentCompanyId = getEffectiveUserCompanyId(userStore);
  const companyId = loadsQuery.job.company?.id ?? currentCompanyId;

  const [isAddingNewLoad, setIsAddingNewLoad] = useState(false);
  const [isApprovingAllLoads, setIsApprovingAllLoads] = useState(false);
  const [ticketsRTUSubscription, setTicketsRTUSubscription] = useState<Subscription>();
  const [ticketDrawerState, setTicketDrawerState] = useState<{
    load?: Load;
    ticket?: Ticket;
  }>();
  const openLoadId = ticketDrawerState
    ? (ticketDrawerState.load?.id ?? LOADLESS_JOB)
    : undefined;

  const crossOrderNav = useCrossOrderNavigation({
    onNavigate: (load, ticket) => setTicketDrawerState({ load, ticket }),
  });

  const effectiveJob = crossOrderNav.crossOrderState?.job ?? loadsQuery.job;
  const effectiveLoads = crossOrderNav.crossOrderState?.loads ?? loadsQuery.loads ?? [];
  const effectiveTickets =
    crossOrderNav.crossOrderState?.tickets ?? loadsQuery.tickets ?? [];

  const isFetching =
    loadsQuery.isFetchingLoads ||
    loadsQuery.isFetchingLoadCycles ||
    loadsQuery.isFetchingTickets;
  const isBusy = isAddingNewLoad || isApprovingAllLoads;
  const hasApprovableLoads =
    loadsQuery.loads?.some((load) => {
      const { isApprovable } = getApprovalStatus(
        load,
        userPermissions,
        Boolean(loadsQuery.job.order?.editable),
      );
      return isApprovable;
    }) ?? false;
  const shouldDisableAddLoadButton = !shouldAllowUserToAddNewLoads(
    loadsQuery.job,
    userStore,
    false,
  );

  const [previousLoad, nextLoad] = useMemo(() => {
    if (!ticketDrawerState?.load?.id) {
      return [null, null];
    }

    const idx = effectiveLoads.findIndex((l) => l.id === ticketDrawerState.load?.id);
    if (idx === -1) return [null, null];

    return [
      idx > 0 ? effectiveLoads[idx - 1] : null,
      idx < effectiveLoads.length - 1 ? effectiveLoads[idx + 1] : null,
    ];
  }, [effectiveLoads, ticketDrawerState?.load?.id]);

  const crossOrderNext = crossOrderNav.getCrossOrderNext(openLoadId, effectiveLoads);
  const crossOrderPrevious = crossOrderNav.getCrossOrderPrevious(
    openLoadId,
    effectiveLoads,
  );

  const loadNumber = useMemo(() => {
    if (ticketDrawerState?.load?.id) {
      const loadIndex = effectiveLoads.findIndex((load) => {
        return load.id === ticketDrawerState.load?.id;
      });

      if (loadIndex >= 0) {
        return loadIndex + 1;
      }
    }
  }, [ticketDrawerState?.load, effectiveLoads]);

  const findTicketByLoadId = useCallback(
    (loadId: string) => {
      return effectiveTickets.find((ticket) => ticket.loadId === loadId);
    },
    [effectiveTickets],
  );

  const handleAddLoadClick = useCallback(async () => {
    const { job } = loadsQuery;

    try {
      setIsAddingNewLoad(true);

      await addLoad(job.id, {
        material: job?.material
          ? ({ id: job.material.id, name: job.material.name } as BasicMaterial)
          : null,
      });

      loadsQuery.refreshLoads();
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_add_load'));
    } finally {
      setIsAddingNewLoad(false);
    }
  }, [loadsQuery.job]);

  const handleApproveAllLoadsClick = useCallback(async () => {
    try {
      setIsApprovingAllLoads(true);

      const approvableLoads = (loadsQuery.loads ?? []).filter(
        (load) =>
          getApprovalStatus(
            load,
            userPermissions,
            Boolean(loadsQuery.job.order?.editable),
          ).isApprovable,
      );

      await approveAllWithTracking(
        approvableLoads,
        (load) => approveLoad(load.id, companyId),
        trackLoadsApproved,
      );

      loadsQuery.refreshLoads();
      loadsQuery.refreshParentJob();
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_approve_load'),
      );
    } finally {
      setIsApprovingAllLoads(false);
    }
  }, [companyId, loadsQuery.loads]);

  const handleTableRowClick = useCallback(
    (loadId: string, ticket?: Ticket) => {
      const load = loadsQuery.loads?.find((load) => load.id === loadId);

      crossOrderNav.resetCrossOrderState();
      setTicketDrawerState({ load, ticket });
    },
    [loadsQuery.loads, crossOrderNav.resetCrossOrderState],
  );

  const subscribeToTicketsRTU = useCallback(() => {
    subscribeToChannel(
      realTimeChannels.TicketUpdateChannel,
      currentCompanyId,
      setTicketsRTUSubscription,
      ({ data }: { data: Ticket_Read }) => {
        const parsedTicket = Ticket.parse(data);
        const queryKey = makeLoadsQueryContextKey(parsedTicket.jobId, 'tickets');

        queryClient.setQueryData<Ticket[]>([queryKey], (oldTickets) => {
          if (oldTickets) {
            const oldTicketIndex = oldTickets.findIndex((ticket) => {
              return ticket.id === parsedTicket.id;
            });

            if (oldTicketIndex >= 0) {
              const newTickets = [...oldTickets];
              newTickets.splice(oldTicketIndex, 1, parsedTicket);

              return newTickets;
            }

            return [...oldTickets, parsedTicket];
          }

          return oldTickets;
        });
        const existingTicket = ticketsStore.tickets.find((t) => t.id === parsedTicket.id);
        if (existingTicket) {
          ticketsStore.updateTicket(parsedTicket);
        } else {
          ticketsStore.setTickets([...ticketsStore.tickets, parsedTicket]);
        }
      },
    );
  }, [currentCompanyId]);

  useEffect(() => {
    setTicketDrawerState((state) => {
      if (!state) {
        return state;
      }

      const updatedTicket = loadsQuery.tickets?.find((storedTicket) => {
        return storedTicket.id === state.ticket?.id;
      });
      const updatedLoad = loadsQuery.loads?.find((load) => load.id === state.load?.id);

      return {
        ...state,
        ticket: updatedTicket ?? state.ticket,
        load: updatedLoad ?? state.load,
      };
    });
  }, [loadsQuery.tickets, loadsQuery.loads]);

  useEffect(() => {
    if (!ticketsRTUSubscription) {
      subscribeToTicketsRTU();
    }
    return () => {
      ticketsRTUSubscription?.unsubscribe();
    };
  }, [ticketsRTUSubscription, subscribeToTicketsRTU]);

  useEffect(() => {
    loadsQuery.refreshEverything();
  }, [loadsQuery.job]);

  useEffect(() => {
    return () => {
      loadsQuery.clearQueryData();
    };
  }, []);

  return (
    <>
      <Box>
        {isFetching && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
            {[0, 1, 2, 3].map((row) => (
              <Skeleton key={row} variant="text" height={20} />
            ))}
          </Box>
        )}

        {!isFetching && (
          <Box sx={{ minHeight: renderLoadsTable ? 0 : ROUTE_MAP_HEIGHT_IN_PX + 50 }}>
            {loadsQuery.loads &&
              loadsQuery.cycles &&
              loadsQuery.tickets &&
              (renderLoadsTable ? (
                renderLoadsTable({
                  cycles: loadsQuery.cycles,
                  job: loadsQuery.job,
                  loads: loadsQuery.loads,
                  onTableRowClick: handleTableRowClick,
                  tickets: loadsQuery.tickets,
                })
              ) : (
                <LoadsTable
                  cycles={loadsQuery.cycles}
                  job={loadsQuery.job}
                  loads={loadsQuery.loads}
                  onTableRowClick={handleTableRowClick}
                  tickets={loadsQuery.tickets}
                />
              ))}
            {!renderLoadsTable && (
              <Box
                sx={{
                  mt: 1.5,
                  alignItems: 'center',
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                }}
              >
                <SmallLoadingButton
                  color="inherit"
                  disabled={shouldDisableAddLoadButton || isBusy}
                  loading={isAddingNewLoad}
                  onClick={handleAddLoadClick}
                  sx={(theme) => ({
                    bgcolor: 'background.paper',
                    border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                  })}
                >
                  {`+ ${t('loads.add')}`}
                </SmallLoadingButton>

                <SmallLoadingButton
                  color="brandV2Yellow"
                  disabled={!hasApprovableLoads || isBusy}
                  loading={isApprovingAllLoads}
                  onClick={handleApproveAllLoadsClick}
                >
                  {t('approvals.files.approve_all_loads_cta')}
                </SmallLoadingButton>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <TicketDrawerV2
        emptyStateMessage={t('ticket_drawer_v2.empty_job_message')}
        isDrawerOpen={Boolean(ticketDrawerState)}
        job={effectiveJob}
        load={ticketDrawerState?.load}
        loadNumber={loadNumber}
        nextUnavailableReason={t('ticket_drawer_v2.end_of_queue')}
        previousUnavailableReason={t('ticket_drawer_v2.start_of_queue')}
        onClose={() => {
          crossOrderNav.resetCrossOrderState();
          setTicketDrawerState(undefined);
        }}
        onLoadUpdated={(load) => {
          if (crossOrderNav.crossOrderState) {
            crossOrderNav.refreshCrossOrderLoads();
          } else {
            loadsQuery.updateStoredLoadById(load.id, load);
          }
        }}
        onShowNextTicketClick={
          crossOrderNav.isFetching
            ? undefined
            : nextLoad
              ? () => {
                  const nextLoadTicket = findTicketByLoadId(nextLoad.id);
                  setTicketDrawerState({
                    load: nextLoad,
                    ticket: nextLoadTicket,
                  });
                }
              : (crossOrderNext ?? undefined)
        }
        onShowPreviousTicketClick={
          crossOrderNav.isFetching
            ? undefined
            : previousLoad
              ? () => {
                  const previousLoadTicket = findTicketByLoadId(previousLoad.id);
                  setTicketDrawerState({
                    load: previousLoad,
                    ticket: previousLoadTicket,
                  });
                }
              : (crossOrderPrevious ?? undefined)
        }
        refreshLoads={() => {
          if (crossOrderNav.crossOrderState) {
            crossOrderNav.refreshCrossOrderLoads();
            loadsQuery.refreshParentJob(crossOrderNav.crossOrderState.orderId);
          } else {
            loadsQuery.refreshLoads();
          }
          loadsQuery.refreshParentJob();
        }}
        refreshTicket={(ticket, isNewTicket) => {
          if (crossOrderNav.crossOrderState) {
            crossOrderNav.updateCrossOrderTicket(ticket, isNewTicket);
          }
          setTicketDrawerState((state) => {
            if (isNewTicket || state?.ticket?.id === ticket.id) {
              return { ...state, ticket };
            }

            return state;
          });
        }}
        ticket={ticketDrawerState?.ticket}
      />
    </>
  );
}
